import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const ChatContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ChatProvider = ({ children }) => {
  const { user } = useAuth(); // NOVO: Obter usuário atual
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const previousUserIdRef = useRef(null); // NOVO: Rastrear mudança de usuário

  // Refs for synchronous access
  const messagesRef = useRef([]);
  const currentConversationIdRef = useRef(null);

  // NOVO: Recarregar conversas quando usuário mudar OU quando fizer login
  useEffect(() => {
    const currentUserId = user?.id;
    
    // CORREÇÃO: Remover primeira verificação para funcionar também no login inicial (null → user)
    if (previousUserIdRef.current !== currentUserId) {
      // Usuário mudou OU fez login! Limpar chat e recarregar do banco
      console.log('🔄 Usuário mudou ou fez login - limpando chat e recarregando do banco');
      sessionStorage.removeItem('chat_currentConversationId');
      sessionStorage.removeItem('chat_messages');
      setCurrentConversationId(null);
      setMessages([]);
      setConversations([]);
      
      // Recarregar conversas do banco APENAS se tem usuário
      if (currentUserId) {
        loadConversations();
      }
    }
    
    previousUserIdRef.current = currentUserId;
  }, [user?.id]);

  // Sync refs with state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    currentConversationIdRef.current = currentConversationId;
  }, [currentConversationId]);

  // Persist to sessionStorage
  useEffect(() => {
    if (currentConversationId && messages.length > 0) {
      try {
        // Garantir que messages não tem referências cíclicas
        const cleanMessages = messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          image: msg.image || undefined
        }));
        
        sessionStorage.setItem('chat_currentConversationId', currentConversationId);
        sessionStorage.setItem('chat_messages', JSON.stringify(cleanMessages));
      } catch (error) {
        console.error('Error saving messages:', error);
        // Se falhar ao salvar, limpar storage
        sessionStorage.removeItem('chat_messages');
      }
    }
  }, [currentConversationId, messages]);

  // Restore from sessionStorage on mount
  useEffect(() => {
    const savedConvId = sessionStorage.getItem('chat_currentConversationId');
    const savedMessages = sessionStorage.getItem('chat_messages');
    
    if (savedConvId && savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        
        // VALIDAÇÃO: Garantir que são objetos de mensagem válidos
        const validMessages = Array.isArray(parsed) 
          ? parsed.filter(msg => msg && typeof msg === 'object' && msg.role && msg.content)
          : [];
        
        if (validMessages.length > 0) {
          setCurrentConversationId(savedConvId);
          setMessages(validMessages);
          console.log('♻️ Chat restored:', savedConvId, 'with', validMessages.length, 'messages');
        } else {
          // Dados inválidos, limpar
          sessionStorage.removeItem('chat_currentConversationId');
          sessionStorage.removeItem('chat_messages');
        }
      } catch (error) {
        console.error('Error parsing saved messages:', error);
        // Limpar dados corrompidos
        sessionStorage.removeItem('chat_currentConversationId');
        sessionStorage.removeItem('chat_messages');
      }
    }
    
    // NÃO carregar conversas aqui - será carregado quando user estiver disponível
    // loadConversations() será chamado pelo useEffect que monitora user?.id
  }, []);

  const loadConversations = async () => {
    try {
      console.log('📥 Carregando conversas do banco de dados...');
      const response = await axios.get(`${API}/chat/conversations`, { withCredentials: true });
      if (response.data.conversations) {
        setConversations(response.data.conversations);
        console.log(`✅ ${response.data.conversations.length} conversas carregadas do banco`);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar conversas do banco:', error);
      // Fallback to localStorage
      const userId = localStorage.getItem('user_id');
      const saved = localStorage.getItem(`chat_conversations_${userId}`);
      if (saved) {
        setConversations(JSON.parse(saved));
      }
    }
  };

  const saveConversations = async (convs) => {
    try {
      await axios.post(`${API}/chat/conversations`, 
        { conversations: convs },
        { withCredentials: true }
      );
    } catch (error) {
      const userId = localStorage.getItem('user_id');
      localStorage.setItem(`chat_conversations_${userId}`, JSON.stringify(convs));
    }
  };

  const addMessage = (message) => {
    setMessages(prev => [...prev, message]);
  };

  const clearMessages = () => {
    setMessages([]);
    setCurrentConversationId(null);
    // Limpar TUDO do sessionStorage relacionado a chat
    sessionStorage.removeItem('chat_currentConversationId');
    sessionStorage.removeItem('chat_messages');
    console.log('🧹 Chat cleared completely');
  };

  const createNewConversation = (initialMessage) => {
    // Limpar conversa anterior primeiro
    clearMessages();
    
    const newId = Date.now().toString();
    setCurrentConversationId(newId);
    setMessages(initialMessage ? [initialMessage] : []);
    console.log('🆕 New conversation created:', newId);
    return newId;
  };

  const updateConversationInList = (id, updatedMessages) => {
    const updated = conversations.map(conv => {
      if (conv.id === id) {
        const title = updatedMessages.length > 0 && updatedMessages[0].role === 'user'
          ? updatedMessages[0].content.substring(0, 30) + '...'
          : conv.title;
        return { ...conv, messages: updatedMessages, title };
      }
      return conv;
    });
    setConversations(updated);
    saveConversations(updated);
  };

  const addConversationToList = (newConv) => {
    const updated = [newConv, ...conversations];
    setConversations(updated);
    saveConversations(updated);
  };

  const loadConversation = (id) => {
    const conv = conversations.find(c => c.id === id);
    if (conv) {
      setCurrentConversationId(id);
      setMessages(conv.messages);
      console.log('📂 Loaded conversation:', id, 'with', conv.messages.length, 'messages');
    }
  };

  const deleteConversation = (id) => {
    const updated = conversations.filter(c => c.id !== id);
    setConversations(updated);
    saveConversations(updated);
    
    if (currentConversationId === id) {
      if (updated.length > 0) {
        loadConversation(updated[0].id);
      } else {
        clearMessages();
      }
    }
  };

  const value = {
    // State
    conversations,
    currentConversationId,
    messages,
    loading,
    
    // Refs (for synchronous access)
    messagesRef,
    currentConversationIdRef,
    
    // Actions
    setLoading,
    addMessage,
    setMessages,
    setCurrentConversationId,
    clearMessages,
    createNewConversation,
    loadConversation,
    deleteConversation,
    updateConversationInList,
    addConversationToList,
    loadConversations
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};
